import { GetStaticProps, GetStaticPropsContext } from 'next';
import { useTranslation } from 'next-i18next';

import Body from 'components/Layout/Body';
import Container from 'components/Layout/Container';
import Footer from 'components/Footer/Footer';
import TopNav from 'components/Navigation/TopNav';
import PageSEO from 'components/SEO/PageSEO';
import PostGrid from 'components/Grids/PostGrid';

import { ContentAssetTypeProps } from '../utilities/ContentAssetTypeProps';
import { useTrackEventViewedPage } from '../hooks/useTrackEventViewedPage';
import { useTrackEventViewedBlogPage } from '../hooks/useTrackEventViewedBlogPage';
import getHrefs from '../helpers/getHrefs';
import BreadCrumbs from '../components/Navigation/BreadCrumbs';
import PageTitle from '../components/Page/PageTitle';
import { ContentAssetTypeModelBuilder } from '../libs/builders/ContentAssetTypeBuilder';
import { useFilters } from '../providers/FiltersProvider';

import { mapNewsletterFields } from './index';

const News: React.FC<Blog.CatInterface> = ({
  contentTypeAsset,
  loadMoreQuery,
  filterQuery,
  locale,
  pageTitle,
  postData,
  postsPerPage,
  seoSchema,
  translations,
}) => {
  useTrackEventViewedPage({ page_name: 'category' });
  useTrackEventViewedBlogPage();

  const { t } = useTranslation('common');
  const { selectedTopics } = useFilters();
  const title = t('title');
  const description = t('desc');
  const hrefs = getHrefs(translations);

  return (
    <>
      <PageSEO
        canonicalUrl="news"
        description={description}
        hrefs={hrefs}
        locale={locale}
        schema={seoSchema}
        title={title}
      />
      <TopNav translations={translations} locale={locale} />
      <Body>
        <Container additionalCssClasses="mt-[40px] mb-[60px]">
          <BreadCrumbs
            locale={locale}
            contentAssetType={pageTitle}
            styles="mt-[40px] mb-[10px] md:mb-[12px]"
          />
          <PageTitle title={contentTypeAsset} />
          <PostGrid
            hasNextPage={postData.GridPosts.pageInfo?.hasNextPage}
            posts={postData.GridPosts.nodes}
            newsletter={mapNewsletterFields(postData.Newsletter)}
            endCursor={postData.GridPosts.pageInfo?.endCursor}
            locale={locale}
            query={loadMoreQuery}
            filterQuery={filterQuery}
            queryArgs={{ contentAssetType: 'News' }}
            filterArgs={{ contentTopics: selectedTopics }}
            queryCount={postsPerPage}
            startCursor={postData.GridPosts.pageInfo?.startCursor}
            showPostPill={false}
          />
        </Container>
        <Footer translations={translations} locale={locale} />
      </Body>
    </>
  );
};

export const getStaticProps: GetStaticProps<Blog.CatInterface> = (
  context: GetStaticPropsContext
) => {
  const locale =
    (context?.locale as Blog.LocaleType) ?? ('en' as Blog.LocaleType);

  const contentAssetTypeModel = ContentAssetTypeModelBuilder.build('news');
  const catProps = new ContentAssetTypeProps(contentAssetTypeModel, locale);

  return catProps.getPageProps();
};

export default News;
